/**
 * Non-colour design tokens: spacing, radii, typography, elevation, motion.
 *
 * Two rules hold this file together:
 *
 * 1. **Values are numbers, not strings.** `space.lg` is `16`, never `'16px'`.
 *    Both react-jss and React's inline `style` prop append `px` to bare numbers
 *    automatically, and React Native takes unitless numbers directly — so one
 *    token set feeds every renderer we might plausibly target. The moment a
 *    token becomes `'16px'` it is web-only.
 *
 * 2. **No token references a component.** These are raw scales. Anything that
 *    encodes intent ("the padding inside a card") belongs in the component that
 *    owns it, not here.
 *
 * The exception to rule 1 is `shadows`, which has to produce a CSS
 * `box-shadow` string. `elevation` holds the portable description and
 * `shadows` holds the web rendering of it; see the note there.
 */

import { palette, withAlpha } from './palette';

/* -------------------------------------------------------------------------- */
/*  Spacing                                                                     */
/* -------------------------------------------------------------------------- */

/**
 * The design system's 8px trame, under the app's names.
 *
 * Values are `--space-1` through `--space-11` from `tokens/spacing.css`, in
 * order. `lg` (16) is the base step — by far the most common gap in the app, so
 * it reads as the neutral choice and the sizes either side of it are the
 * deliberate ones.
 *
 * The wide upper steps are not padding for its own sake: the brand note against
 * this scale is *« laisser respirer les autres »*, and the generous end of it is
 * meant to be used.
 */
export const space = {
  none: 0,
  xs: 4,
  sm: 8,
  md: 12,
  lg: 16,
  xl: 24,
  '2xl': 32,
  '3xl': 40,
  '4xl': 56,
  '5xl': 72,
  '6xl': 96,
  '7xl': 128,
} as const;

export type Space = keyof typeof space;

/* -------------------------------------------------------------------------- */
/*  Radii                                                                       */
/* -------------------------------------------------------------------------- */

/**
 * Radii, from `tokens/spacing.css`.
 *
 * Generous and soft: the identity is calm and flat, so rounding does the work
 * that borders and shadows used to. The design system's note against
 * `--radius-pill` is that buttons and tags are capsules **by default** — that is
 * expressed in `antdTheme.ts`, not by every call site remembering to ask.
 */
export const radius = {
  none: 0,
  sm: 8,
  md: 16,
  lg: 24,
  xl: 32,
  /** Fully rounded ends. Large enough to act as a pill at any height we use. */
  pill: 999,
  /** `--radius-circle`. The one radius that is a string: a percentage. */
  circle: '50%',
} as const;

export type Radius = keyof typeof radius;

/* -------------------------------------------------------------------------- */
/*  Typography                                                                  */
/* -------------------------------------------------------------------------- */

/**
 * Font families.
 *
 * *« Une seule voix, toutes les intonations »* — Cera Pro is the only
 * typeface, so `display` and `text` resolve to the same family. The two names
 * are kept because they are the seam: if a separate display face is ever
 * adopted, it lands here and in `fonts.css` and nothing else changes.
 *
 * Declared as *family names* with a system fallback stack, which is the form
 * both CSS and React Native understand. The files are wired up in
 * `design/fonts.css`; with no font files present every name falls through to
 * the system stack and the app still renders.
 */
const CERA = "'Cera Pro', Poppins, -apple-system, BlinkMacSystemFont, 'Segoe UI', Helvetica, Arial, sans-serif";

export const fontFamily = {
  /** Titles and display copy. See `fontWeight` for why they are not Light. */
  display: CERA,
  /** Body copy, labels, controls. */
  text: CERA,
  mono: "source-code-pro, Menlo, Monaco, Consolas, 'Courier New', monospace",
} as const;

/**
 * Weights, matching the cuts `fonts.css` actually serves.
 *
 * Headings are `regular` and never bold. The brand sets titles in Light, which
 * is right at slide sizes but reads as fragile at the 28-34px the app actually
 * uses, so the app takes the step above — still visibly thinner than the bold
 * default, which is the part of the signature that matters here.
 * `medium` is the design system's working weight for overlines, buttons,
 * labels and keywords; `bold` is for a word inside body copy that must carry
 * emphasis. There is deliberately no `semibold`: Cera Pro has no 600 cut, so a
 * request for 600 resolves up to Bold anyway, and naming a weight the family
 * cannot set invites exactly the mismatch that made `<strong>` render Black.
 */
export const fontWeight = {
  thin: 100,
  light: 300,
  regular: 400,
  medium: 500,
  bold: 700,
  black: 900,
} as const;

/**
 * The type scale.
 *
 * From `--fs-*` in the design system, which is authored for slides: it runs to
 * 96px and puts h1 at 40. Everything from h4 down is taken verbatim, because
 * body copy does not care what it is rendered on. The two largest heading steps
 * are pulled in — h1 40→34, h2 32→28 — because the app's widest common viewport
 * is 390px, where a 40px heading takes three lines for an ordinary sentence.
 * The ratios between steps are preserved.
 */
export const fontSize = {
  /** `--fs-overline` */
  xxs: 11,
  /** `--fs-caption` */
  xs: 12,
  /** `--fs-body-s` */
  sm: 14,
  /** `--fs-body` */
  md: 16,
  /** `--fs-body-l` */
  lg: 18,
  /** `--fs-h4` */
  xl: 20,
  /** `--fs-h3` */
  '2xl': 24,
  /** `--fs-h2`, reduced for mobile */
  '3xl': 28,
  /** `--fs-h1`, reduced for mobile */
  '4xl': 34,
} as const;

/** Unitless multipliers — portable, and the form both CSS and RN prefer. */
export const lineHeight = {
  /** `--lh-title` */
  tight: 1.08,
  /** `--lh-snug` */
  snug: 1.25,
  /** `--lh-body` */
  normal: 1.55,
  /** `--lh-loose` */
  relaxed: 1.7,
} as const;

/**
 * Letter spacing, in px.
 *
 * The design system expresses these in `em`, which scales with the text. px is
 * what keeps the token layer numeric and therefore portable, so these are the
 * `em` values resolved at the size each one is actually used at: `--ls-title`
 * (-0.01em) at the heading sizes, `--ls-overline` (0.18em) at 11px.
 */
export const letterSpacing = {
  tight: -0.5,
  normal: 0,
  wide: 0.4,
  wider: 2,
} as const;

/**
 * The type scale: the named roles a piece of text can play.
 *
 * Components should reach for one of these rather than assembling a size and a
 * weight by hand — that is what keeps the app from drifting back into the 169
 * ad-hoc `fontSize` declarations this replaces. Rendered by the `Text` and
 * `Title` primitives in `components/ui`.
 */
export const typography = {
  display: {
    fontFamily: fontFamily.display,
    fontSize: fontSize['4xl'],
    fontWeight: fontWeight.regular,
    lineHeight: lineHeight.tight,
    letterSpacing: letterSpacing.tight,
  },
  h1: {
    fontFamily: fontFamily.display,
    fontSize: fontSize['3xl'],
    fontWeight: fontWeight.regular,
    lineHeight: lineHeight.tight,
    letterSpacing: letterSpacing.tight,
  },
  h2: {
    fontFamily: fontFamily.display,
    fontSize: fontSize['2xl'],
    fontWeight: fontWeight.regular,
    lineHeight: lineHeight.snug,
    letterSpacing: letterSpacing.normal,
  },
  h3: {
    fontFamily: fontFamily.display,
    fontSize: fontSize['2xl'],
    fontWeight: fontWeight.regular,
    lineHeight: lineHeight.snug,
    letterSpacing: letterSpacing.normal,
  },
  h4: {
    fontFamily: fontFamily.display,
    fontSize: fontSize.xl,
    fontWeight: fontWeight.medium,
    lineHeight: lineHeight.snug,
    letterSpacing: letterSpacing.normal,
  },
  bodyLarge: {
    fontFamily: fontFamily.text,
    fontSize: fontSize.lg,
    fontWeight: fontWeight.regular,
    lineHeight: lineHeight.relaxed,
    letterSpacing: letterSpacing.normal,
  },
  body: {
    fontFamily: fontFamily.text,
    fontSize: fontSize.md,
    fontWeight: fontWeight.regular,
    lineHeight: lineHeight.normal,
    letterSpacing: letterSpacing.normal,
  },
  bodySmall: {
    fontFamily: fontFamily.text,
    fontSize: fontSize.sm,
    fontWeight: fontWeight.regular,
    lineHeight: lineHeight.normal,
    letterSpacing: letterSpacing.normal,
  },
  label: {
    fontFamily: fontFamily.text,
    fontSize: fontSize.sm,
    fontWeight: fontWeight.medium,
    lineHeight: lineHeight.snug,
    letterSpacing: letterSpacing.normal,
  },
  caption: {
    fontFamily: fontFamily.text,
    fontSize: fontSize.xs,
    fontWeight: fontWeight.regular,
    lineHeight: lineHeight.snug,
    letterSpacing: letterSpacing.normal,
  },
  overline: {
    fontFamily: fontFamily.text,
    fontSize: fontSize.xxs,
    fontWeight: fontWeight.medium,
    lineHeight: lineHeight.snug,
    letterSpacing: letterSpacing.wider,
  },
} as const;

export type TypographyVariant = keyof typeof typography;

/* -------------------------------------------------------------------------- */
/*  Elevation                                                                   */
/* -------------------------------------------------------------------------- */

/**
 * Elevation, described as portable data.
 *
 * The old app leaned on hard offset shadows (`0px 5px rgba(100,100,100,0.2)`)
 * which read as chunky and dated. The new identity is flat, so these are
 * deliberately soft and shallow — most surfaces should use `none` or `xs` and
 * let the sand/white surface contrast do the separating.
 *
 * The shadow colour is the deep teal rather than neutral black: a black shadow
 * over a warm sand background goes grey and dirty, whereas a teal-tinted one
 * stays in the family.
 */
export const elevation = {
  none: null,
  xs: { color: palette.teal[700], opacity: 0.04, offsetX: 0, offsetY: 1, blur: 2, spread: 0 },
  /** `--shadow-card` */
  sm: { color: palette.teal[700], opacity: 0.06, offsetX: 0, offsetY: 2, blur: 8, spread: 0 },
  /** `--shadow-raised` */
  md: { color: palette.teal[700], opacity: 0.1, offsetX: 0, offsetY: 12, blur: 32, spread: 0 },
  /** `--shadow-modal` */
  lg: { color: palette.teal[700], opacity: 0.16, offsetX: 0, offsetY: 24, blur: 64, spread: 0 },
} as const;

export type Elevation = keyof typeof elevation;

/** Renders one elevation token as a CSS `box-shadow` value. */
export function shadow(level: Elevation): string {
  const e = elevation[level];
  if (!e) return 'none';
  return `${e.offsetX}px ${e.offsetY}px ${e.blur}px ${e.spread}px ${withAlpha(e.color, e.opacity)}`;
}

/** Pre-rendered CSS `box-shadow` strings, for use in `createUseStyles` blocks. */
export const shadows = {
  none: 'none',
  xs: shadow('xs'),
  sm: shadow('sm'),
  md: shadow('md'),
  lg: shadow('lg'),
} as const;

/**
 * Renders one elevation token as a CSS `filter: drop-shadow(...)` value.
 *
 * `drop-shadow` follows the alpha channel of the element rather than its box,
 * which is what you want for a shadow under an image with transparency or an
 * SVG. It takes no spread, so that component of the token is dropped.
 */
export function dropShadow(level: Elevation): string {
  const e = elevation[level];
  if (!e) return 'none';
  return `drop-shadow(${e.offsetX}px ${e.offsetY}px ${e.blur}px ${withAlpha(e.color, e.opacity)})`;
}

/* -------------------------------------------------------------------------- */
/*  Motion                                                                      */
/* -------------------------------------------------------------------------- */

/**
 * Durations in milliseconds, from `--dur-*`. Calm interface: nothing snaps,
 * nothing lingers.
 */
export const duration = {
  /** No animation at all — not the design system's `--dur-instant`, which is 120ms. */
  none: 0,
  /** `--dur-instant` */
  fast: 120,
  /** `--dur-rapide` */
  quick: 180,
  /** `--dur-standard` */
  base: 260,
  /** `--dur-calme` */
  slow: 420,
} as const;

export const easing = {
  /** `--ease-standard` */
  standard: 'cubic-bezier(0.22, 0.61, 0.36, 1)',
  /** `--ease-entree` */
  decelerate: 'cubic-bezier(0.16, 1, 0.3, 1)',
  /** `--ease-sortie` */
  accelerate: 'cubic-bezier(0.4, 0, 1, 1)',
} as const;

/**
 * Interaction transforms, from `--hover-lift` / `--press-scale`.
 *
 * Kept as strings because they are CSS `transform` values; a renderer that is
 * not a browser would need its own form of these.
 */
export const motion = {
  hoverLift: 'translateY(-2px)',
  pressScale: 'scale(0.985)',
} as const;

/* -------------------------------------------------------------------------- */
/*  Layout                                                                      */
/* -------------------------------------------------------------------------- */

/**
 * The single breakpoint the app actually distinguishes, in px.
 *
 * Kept as a number so `useIsSmallScreen` and any future RN `Dimensions` check
 * can share it rather than each hard-coding their own threshold.
 */
export const breakpoint = {
  small: 768,
} as const;

/**
 * Fixed sizes for chrome that other layout has to account for (e.g. content
 * that must clear the bottom navigation).
 */
export const size = {
  bottomNavHeight: 60,
  topBarHeight: 56,
  touchTarget: 44,
} as const;

/**
 * The height of a full-screen page shell.
 *
 * Four screens had this `calc()` copied out by hand, which is three chances for
 * them to drift apart. `dvh` rather than `vh` because mobile browsers shrink
 * the viewport as the URL bar retracts and `vh` does not follow; the safe-area
 * inset then keeps content clear of the notch on native.
 *
 * `--gg-safe-area-top` rather than `env(safe-area-inset-top)` directly: it is
 * defined in `index.html` as `var(--safe-area-inset-top, env(...))`, which lets
 * native set a measured value and is what fixes the Android keyboard/safe-area
 * behaviour. Reading `env()` here would quietly opt every full-screen shell out
 * of that fix.
 *
 * This is the one layout value in the token layer that is a CSS string rather
 * than a number — neither `env()` nor a custom property has a numeric
 * equivalent to hand to a renderer that is not a browser. If the app ever needs
 * to run outside a webview, this is the single place that has to become a
 * measured value.
 */
export const screenHeight = 'calc(100dvh - var(--gg-safe-area-top))';

export const zIndex = {
  base: 0,
  raised: 1,
  sticky: 10,
  navigation: 100,
  overlay: 1000,
  modal: 1010,
  notification: 1050,
} as const;
